import { PageFooterButtons } from '../../../widgets/headers';
import { PageHeader } from '../../../widgets/pageHeader';
import { PrimaryButton, TextButton } from '../../../widgets/buttons';
import { removeNotification, showErrorNotification, showInfoNotification, showNotification } from '../../../widgets/notificationToaster';

import { useInfiniteScroll } from '../../../widgets/hooks/useInfiniteScroll';
import { ContainerBase } from '../../../widgets/layouts';
import { debounce } from 'lodash';
import React, { FC, Suspense, useCallback, useEffect, useState, useTransition } from 'react';
import { graphql, useLazyLoadQuery, useMutation, usePaginationFragment } from 'react-relay';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Loader from 'src/components/widgets/loader';
import { Relay } from 'src/relay';
import { PaletteType } from 'src/shared/enums';
import { Center } from 'src/shared/globals';
import { backofficeUrl, createEmptyPalette, T, toGlobalID, validateAndFilterCurrentPaletteV2 } from 'src/shared/helpers';
import { usePaletteColors } from 'src/shared/hooks/usePaletteColors';
import { useValidationContext, ValidationStateProvider } from 'src/shared/validation';
import { paletteColorsPageSaveMutation } from './__generated__/paletteColorsPageSaveMutation.graphql';
import { PaletteColorInput, paletteColorsPageUpdateMutation } from './__generated__/paletteColorsPageUpdateMutation.graphql';
import { paletteColorsPage_colors$key } from './__generated__/paletteColorsPage_colors.graphql';
import { paletteColorsPage_colorsQuery } from './__generated__/paletteColorsPage_colorsQuery.graphql';
import { paletteColorsPage_paletteQuery } from './__generated__/paletteColorsPage_paletteQuery.graphql';
import PaletteColorsContent from './paletteColorsContent';
import PantoneLibraryPage from './pantoneLibrary/pantoneLibraryPage';

// Constants for view types
const VIEW_TYPES = {
    PALETTE_COLORS: 'paletteColors',
    PANTONE_LIBRARY: 'pantoneLibrary'
} as const;

type ViewType = (typeof VIEW_TYPES)[keyof typeof VIEW_TYPES];

const paletteQuery = graphql`
    query paletteColorsPage_paletteQuery($paletteId: ID!) {
        palette(id: $paletteId) {
            id
            name
            useConversionTable
            paletteTypeID
            isZakekeDefault
            colors {
                id
                name
                rgbHexCode
                cmykHexCode
                spotColorName
            }
        }
    }
`;

const colorsQuery = graphql`
    fragment paletteColorsPage_colors on Query
    @refetchable(queryName: "colorsQuery")
    @argumentDefinitions(
        first: { type: "Int", defaultValue: 70 }
        after: { type: "String", defaultValue: "1" }
        search: { type: "String", defaultValue: null }
        id: { type: "String", defaultValue: null }
        orderBy: { type: "String", defaultValue: null }
        useConversionTable: { type: "Boolean", defaultValue: null }
    ) {
        paletteColors(first: $first, after: $after, search: $search, id: $id, orderBy: $orderBy, useConversionTable: $useConversionTable)
            @connection(key: "colors_paletteColors") {
            pageInfo {
                hasNextPage
                endCursor
            }
            edges {
                cursor
                node {
                    id
                    colorID
                    name
                    rgbHexCode
                    cmykHexCode
                    spotColorName
                }
            }
        }
    }
`;

const PaletteColorsPageContent: FC = () => {
    // const invalidate = useRelayStore((state) => state.invalidateRelayStore);
    const [searchParams] = useSearchParams();
    const paletteId = searchParams.get('palette_id');
    const navigate = useNavigate();
    const validationContext = useValidationContext();
    const [isFilterPending, startFilterTransition] = useTransition();
    const [, startLoadingMoreTransition] = useTransition();
    const [view, setView] = React.useState<ViewType>(VIEW_TYPES.PALETTE_COLORS);
    const [search, setSearch] = useState('');
    const [searchFilter, setSearchFilter] = useState('');
    const [replacingColorID, setReplacingColorID] = useState<string | null>(null);
    // Track which color is being replaced by its unique ID

    const paletteData = paletteId
        ? useLazyLoadQuery<paletteColorsPage_paletteQuery>(paletteQuery, { paletteId: toGlobalID(paletteId, 'Palette') }, { fetchPolicy: 'network-only' })
        : null;

    const paletteColorsPage_colorsQuery = paletteId
        ? useLazyLoadQuery<paletteColorsPage_colorsQuery>(
              graphql`
                  query paletteColorsPage_colorsQuery(
                      $first: Int
                      $after: String
                      $search: String
                      $id: String
                      $orderBy: String
                      $useConversionTable: Boolean
                  ) {
                      ...paletteColorsPage_colors
                          @arguments(first: $first, after: $after, search: $search, id: $id, orderBy: $orderBy, useConversionTable: $useConversionTable)
                  }
              `,
              { first: 70, after: '1', search: searchFilter || null, id: toGlobalID(paletteId, 'Palette'), orderBy: 'DESC', useConversionTable: true },
              { fetchPolicy: 'network-only' }
          )
        : null;

    const { data, loadNext, refetch, hasNext, isLoadingNext } = paletteColorsPage_colorsQuery
        ? usePaginationFragment<paletteColorsPage_colorsQuery, paletteColorsPage_colors$key>(colorsQuery, paletteColorsPage_colorsQuery)
        : { data: null, loadNext: () => {}, refetch: () => {}, hasNext: false, isLoadingNext: false };

    useInfiniteScroll(
        () =>
            startLoadingMoreTransition(() => {
                loadNext(70);
            }),
        hasNext,
        null, // Pass the scroll container element
        isLoadingNext
    );

    const {
        currentColors,
        addedColors,
        editedColors,
        deletedColors,
        setEditedColors,
        setCurrentColors,
        addColor,
        addColors,
        deleteColor,
        updateColor,
        resetColor,
        removeEmptyColors,
        handleCurrentColorsChange,
        currentPalette,
        handlePaletteNameChange,
        handlePaletteTypeChange,
        findColorById,
        isTemporaryColor,
        filterSearchedColors
    } = usePaletteColors(paletteData?.palette || createEmptyPalette(), data?.paletteColors?.edges?.map((edge) => edge.node) || [], !!searchFilter);

    const handleShouldShowRadioButtons = (): boolean => {
        return (
            !currentPalette.isZakekeDefault &&
            (currentPalette.id === null || currentPalette.id === undefined) &&
            currentColors[0] &&
            currentColors[0].rgbHexCode === ''
        );
    };

    const setSearchDebounced = useCallback(
        debounce((filter: string) => {
            startFilterTransition(() => {
                setSearchFilter(filter);
            });
        }, 300),
        []
    );

    const handleSearchColor = (search: string) => {
        // setSnapshotColorIndexes(currentColors.map((color, index) => index));
        setSearch(search);
        setSearchDebounced(search);
    };

    const handleSetReplacingColor = (selectedColor: PaletteColorInput) => {
        // All colors now have IDs (either real IDs from database or temporary IDs for added colors)
        if (selectedColor.id) {
            setReplacingColorID(selectedColor.id);

            // If it's not a temporary color and not already in editedColors, add it
            if (!isTemporaryColor(selectedColor.id) && editedColors.every((color) => color.id !== selectedColor.id)) {
                setEditedColors((prev) => [selectedColor, ...prev]);
            }
        }
    };

    const handlePantoneIconClick = (index: number, color: PaletteColorInput) => {
        // Store the index of the color being replaced
        handleSetReplacingColor(color);

        // Navigate to pantone library for single color selection
        setView(VIEW_TYPES.PANTONE_LIBRARY);
        navigate(backofficeUrl(buildPaletteUrl(VIEW_TYPES.PANTONE_LIBRARY)));
    };

    // Helper function to build URLs with consistent parameters
    const buildPaletteUrl = (view: ViewType) => {
        const basePath =
            view === VIEW_TYPES.PALETTE_COLORS ? '/settings/color-palette-settings/palette' : '/settings/color-palette-settings/palette/pantone-library';

        if (!paletteId) return basePath;

        const params = new URLSearchParams({
            palette_id: paletteId
        });

        return `${basePath}?${params.toString()}`;
    };

    const handleChooseFromLibraryClick = () => {
        setSearch('');
        setSearchFilter('');
        setView(VIEW_TYPES.PANTONE_LIBRARY);
        navigate(backofficeUrl(buildPaletteUrl(VIEW_TYPES.PANTONE_LIBRARY)));
    };

    const handleAddColors = (colors: PaletteColorInput[]) => {
        addColors(colors);
        removeEmptyColors();
        setView(VIEW_TYPES.PALETTE_COLORS);
        navigate(backofficeUrl(buildPaletteUrl(VIEW_TYPES.PALETTE_COLORS)));
    };

    const handleReplaceColor = (selectedColors: PaletteColorInput[]) => {
        if (selectedColors.length === 0 || replacingColorID === null) {
            // No color selected or no replacement target, just go back
            setReplacingColorID(null);
            setSearch('');
            setSearchFilter('');
            setView(VIEW_TYPES.PALETTE_COLORS);
            navigate(backofficeUrl(buildPaletteUrl(VIEW_TYPES.PALETTE_COLORS)));
            return;
        }

        const newColor = selectedColors[0]; // Only take the first selected color
        const { color: colorToReplace, index: colorIndex } = findColorById(replacingColorID);

        if (colorToReplace && colorIndex !== -1) {
            if (isTemporaryColor(replacingColorID)) {
                // If the color being replaced is a temporary added color, treat it as an addition
                updateColor(colorIndex, newColor);
            } else {
                // If the color being replaced is an existing color, treat it as an edit
                const editIndex = editedColors.findIndex((color) => color.id === replacingColorID);
                updateColor(editIndex !== -1 ? editIndex : colorIndex, newColor, editIndex !== -1);
            }
        }

        // Clear replacement state and navigate back
        setReplacingColorID(null);
        setSearch('');
        setSearchFilter('');
        setView(VIEW_TYPES.PALETTE_COLORS);
        navigate(backofficeUrl(buildPaletteUrl(VIEW_TYPES.PALETTE_COLORS)));
    };

    const handleBackClick = () => {
        setSearch('');
        setSearchFilter('');
        if (view === VIEW_TYPES.PALETTE_COLORS) {
            // Navigate back to Color Library Settings
            navigate(backofficeUrl('/settings/color-palette-settings'));
        } else {
            // From pantone library, go back to palette colors
            setView(VIEW_TYPES.PALETTE_COLORS);
            navigate(backofficeUrl(buildPaletteUrl(VIEW_TYPES.PALETTE_COLORS)));
        }
    };

    useEffect(() => {
        if (paletteId && data?.paletteColors?.edges) {
            const filteredColors = filterSearchedColors(data?.paletteColors?.edges?.map((edge) => edge.node) || [], searchFilter);
            setCurrentColors(filteredColors);
        }
    }, [data]);

    const [updatePalette, isUpdatingPalette] = useMutation<paletteColorsPageUpdateMutation>(graphql`
        mutation paletteColorsPageUpdateMutation($input: PaletteInput!) {
            updatePalette(input: $input) {
                palette {
                    ...paletteFragment_palette @relay(mask: false)
                }
            }
        }
    `);
    const [savePalette, isSavingPalette] = useMutation<paletteColorsPageSaveMutation>(graphql`
        mutation paletteColorsPageSaveMutation($input: PaletteInput!) {
            createPalette(input: $input) {
                palette {
                    ...paletteFragment_palette @relay(mask: false)
                }
            }
        }
    `);

    const handleSaveClick = () => {
        if (!validationContext.submit()) return;

        const { isPaletteValid, errorMessage, input } = validateAndFilterCurrentPaletteV2(currentPalette, paletteId !== null);

        if (isPaletteValid) {
            if (paletteId) {
                const updatePaletteNotification = showInfoNotification({
                    message: T._(`Updating Palette... please don't exit from page.`, 'Admin'),
                    dismiss: undefined
                });

                updatePalette({
                    variables: {
                        input
                    },
                    onCompleted: () => {
                        removeNotification(updatePaletteNotification);
                        showNotification({
                            type: 'success',
                            message: T._('Palette updated.', 'Admin'),
                            dismiss: {
                                duration: 5000
                            }
                        });
                        navigate(backofficeUrl('/settings/color-palette-settings?palette=updated'));
                    },
                    onError: () => {
                        removeNotification(updatePaletteNotification);
                        showNotification({
                            type: 'danger',
                            message: T._('Error updating palette.', 'Admin'),
                            dismiss: {
                                duration: 5000
                            }
                        });
                    }
                });
            } else {
                const savePaletteNotification = showInfoNotification({
                    message: T._(`Saving Palette... please don't exit from page.`, 'Admin'),
                    dismiss: undefined
                });

                savePalette({
                    variables: {
                        input
                    },
                    onCompleted: () => {
                        removeNotification(savePaletteNotification);
                        showNotification({
                            type: 'success',
                            message: T._('Palette saved.', 'Admin'),
                            dismiss: {
                                duration: 5000
                            }
                        });
                        navigate(backofficeUrl('/settings/color-palette-settings?palette=created'));
                    },
                    onError: (error) => {
                        console.error('Error saving palette:', error);
                        removeNotification(savePaletteNotification);
                        showNotification({
                            type: 'danger',
                            message: T._('Error saving palette.', 'Admin'),
                            dismiss: {
                                duration: 5000
                            }
                        });
                    }
                });
            }
        } else {
            showErrorNotification({
                message: T._(errorMessage || 'Palette is invalid.', 'Admin'),
                dismiss: {
                    duration: 9000
                }
            });
        }
    };

    return (
        <>
            {view === VIEW_TYPES.PALETTE_COLORS && (
                <>
                    <ContainerBase>
                        <PageHeader
                            title={'Your Color Palette'}
                            pageKey='pantone-cmyk-palettes'
                            breadcumbCurrentLabel={paletteId ? T._('Edit palette', 'Admin') : T._('New palette', 'Admin')}
                            backLabel={T._('Back', 'Admin')}
                            isBackVisible={true}
                            onBackClick={handleBackClick}
                        ></PageHeader>
                        <>
                            <PaletteColorsContent
                                currentPalette={currentPalette}
                                currentColors={currentColors}
                                onPaletteNameChange={handlePaletteNameChange}
                                onChangeColorType={handlePaletteTypeChange}
                                onAdd={addColor}
                                onEdit={updateColor}
                                onDelete={deleteColor}
                                onReset={resetColor}
                                onPantoneIconClick={handlePantoneIconClick}
                                onSearchColor={handleSearchColor}
                                searchValue={search}
                                hasColors={!!paletteId}
                                shouldShowRadioButtons={handleShouldShowRadioButtons()}
                                isPending={isFilterPending}
                                paletteNameValidationKey='paletteColorsName'
                                paletteColorsValidationKey='paletteColorsRows'
                                validatePaletteColors={(value) => !validateAndFilterCurrentPaletteV2(value, paletteId !== null).isPaletteValid}
                                paletteColorsValidationMessage={T._('Every color must have the required code, a name, and a valid RGB hex code.', 'Admin')}
                            />

                            {hasNext && isLoadingNext && (
                                <Center style={{ marginTop: '15px' }}>
                                    <Loader />
                                </Center>
                            )}
                        </>
                    </ContainerBase>
                    {!currentPalette.isZakekeDefault && (
                        <PageFooterButtons
                            $fixed={true}
                            leftContainerContent={
                                <>
                                    {currentPalette.paletteTypeID === PaletteType.Pantone && (
                                        <TextButton onClick={handleChooseFromLibraryClick}>{T._('Start with pre-configured colors', 'Admin')}</TextButton>
                                    )}
                                </>
                            }
                        >
                            <PrimaryButton onClick={handleSaveClick} disabled={isSavingPalette || isUpdatingPalette}>
                                {paletteId ? T._('Save palette', 'Admin') : T._('Create palette', 'Admin')}
                            </PrimaryButton>
                        </PageFooterButtons>
                    )}
                </>
            )}

            {view === VIEW_TYPES.PANTONE_LIBRARY && (
                <PantoneLibraryPage
                    onAddColors={replacingColorID ? handleReplaceColor : handleAddColors}
                    onBackClick={handleBackClick}
                    singleSelection={replacingColorID !== null}
                />
            )}
        </>
    );
};

const PaletteColorsPage: FC = () => {
    return (
        <Relay>
            <Suspense
                fallback={
                    <Center style={{ height: '100vh' }}>
                        <Loader />
                    </Center>
                }
            >
                <ValidationStateProvider>
                    <PaletteColorsPageContent />
                </ValidationStateProvider>
            </Suspense>
        </Relay>
    );
};

export default PaletteColorsPage;
